import {
  ShowBase,
  useShowContext,
  useUpdate,
  useRefresh,
  useRecordContext,
  useDelete,
  useNotify,
} from "ra-core";
import { Link, useNavigate } from "react-router";
import { useState, useMemo, useEffect } from "react";
import {
  ArrowLeft,
  FileText,
  Briefcase,
  Calendar,
  DollarSign,
  Plus,
  Trash2,
  Save,
  Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ReferenceField } from "@/components/admin/reference-field";
import type {
  Proposal,
  LineItem,
} from "../../providers/fakerest/dataGenerator/types";

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  sent: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  accepted: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const statusChoices = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
];

export const ProposalShow = () => {
  return (
    <ShowBase>
      <ProposalShowContent />
    </ShowBase>
  );
};

const ProposalShowContent = () => {
  const { record, isPending } = useShowContext<Proposal>();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const notify = useNotify();

  const handleDelete = () => {
    deleteOne(
      "proposals",
      { id: record!.id, previousData: record },
      {
        onSuccess: () => {
          notify("Proposal deleted successfully", { type: "success" });
          navigate("/proposals");
        },
        onError: () => {
          notify("Failed to delete proposal", { type: "error" });
        },
      },
    );
  };

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [status, setStatus] = useState<string>("");
  const [validUntil, setValidUntil] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize state when record loads
  useEffect(() => {
    if (record) {
      setLineItems(record.line_items || []);
      setStatus(record.status);
      setValidUntil(record.valid_until || "");
    }
  }, [record]);

  const total = useMemo(
    () =>
      lineItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0),
    [lineItems],
  );

  if (isPending || !record) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);

  const handleLineItemChange = (
    index: number,
    field: keyof LineItem,
    value: string | number,
  ) => {
    const newItems = [...lineItems];
    if (field === "description") {
      newItems[index].description = value as string;
    } else if (field === "quantity") {
      newItems[index].quantity = Number(value) || 0;
    } else if (field === "unit_price") {
      newItems[index].unit_price = Number(value) || 0;
    }
    setLineItems(newItems);
    setHasChanges(true);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { description: "", quantity: 1, unit_price: 0 },
    ]);
    setHasChanges(true);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    setHasChanges(true);
  };

  const handleValidUntilChange = (newDate: string) => {
    setValidUntil(newDate);
    setHasChanges(true);
  };

  const handleSave = () => {
    update(
      "proposals",
      {
        id: record.id,
        data: {
          line_items: lineItems,
          total,
          status,
          valid_until: validUntil,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          setHasChanges(false);
          refresh();
        },
      },
    );
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Back button and delete */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/proposals"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Proposals
        </Link>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={isDeleting}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Proposal
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Proposal</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this proposal? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Header Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Proposal #{record.id}
            </CardTitle>
            <Badge className={statusColors[status] || statusColors.draft}>
              {status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job Reference */}
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Job:</span>
            <ReferenceField source="job_id" reference="jobs" link={false}>
              <JobLink jobId={record.job_id} />
            </ReferenceField>
          </div>

          {/* Valid Until */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Valid Until:</span>
            <Input
              type="date"
              value={validUntil}
              onChange={(e) => handleValidUntilChange(e.target.value)}
              className="w-40 h-8"
            />
          </div>

          {/* Status Selector */}
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Status:</span>
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-40">
                <SelectValue>
                  <Badge className={statusColors[status] || statusColors.draft}>
                    {statusChoices.find((c) => c.value === status)?.label ||
                      status}
                  </Badge>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {statusChoices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    <Badge className={statusColors[choice.value]}>
                      {choice.label}
                    </Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Created Date */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Created:</span>
            <span className="text-sm">
              {new Date(record.created_at).toLocaleDateString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Line Items Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Line Items
            </CardTitle>
            <Button variant="outline" size="sm" onClick={addLineItem}>
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Description</TableHead>
                <TableHead className="w-[15%]">Qty</TableHead>
                <TableHead className="w-[20%]">Unit Price</TableHead>
                <TableHead className="w-[15%] text-right">Total</TableHead>
                <TableHead className="w-[10%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      value={item.description}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "description",
                          e.target.value,
                        )
                      }
                      placeholder="Description"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={(e) =>
                        handleLineItemChange(index, "quantity", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "unit_price",
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.quantity * item.unit_price)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLineItem(index)}
                      disabled={lineItems.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Total */}
          <div className="flex justify-end mt-4 pt-4 border-t">
            <div className="text-right">
              <span className="text-sm text-muted-foreground mr-4">Total:</span>
              <span className="text-2xl font-bold">
                {formatCurrency(total)}
              </span>
            </div>
          </div>

          {/* Save Button */}
          {hasChanges && (
            <div className="flex justify-end mt-4">
              <Button onClick={handleSave} disabled={isUpdating}>
                <Save className="h-4 w-4 mr-2" />
                {isUpdating ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component to display job link
const JobLink = ({ jobId }: { jobId: number }) => {
  const record = useRecordContext();
  if (!record) return <span>#{jobId}</span>;

  return (
    <Link
      to={`/jobs/${jobId}/show`}
      className="text-sm text-blue-600 hover:underline"
    >
      {record.address || `#${jobId}`}
    </Link>
  );
};
